import { useState } from "react";
import { ArrowUpRight, Sprout, Scissors, PenTool, Droplets, Leaf, ArrowRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const SERVICES = [
  {
    n: "01",
    title: "Manutenzione Giardini",
    Icon: Sprout,
    desc: "Cura periodica di prati, siepi, aiuole. Tagli, sfalci, rifiniture e pulizia stagionale.",
    long:
      "Mantengo il tuo giardino ordinato e in salute tutto l'anno: taglio e cura del prato, sfalcio, rifinitura di siepi e bordure, pulizia stagionale e raccolta del fogliame. Interventi programmati con attrezzatura professionale, per un verde sempre curato in ogni stagione.",
    image:
      "https://images.unsplash.com/photo-1753006990440-ecdba4925cec?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  },
  {
    n: "02",
    title: "Potatura",
    Icon: Scissors,
    desc: "Potature di alberi, arbusti e siepi nel rispetto dei tempi vegetativi e della specie.",
    long:
      "Poto alberi, arbusti e siepi rispettando i tempi vegetativi e la fisiologia di ogni specie. Tecniche corrette per favorire fioritura, sicurezza e longevità della pianta, con strumenti professionali sempre disinfettati e ordinato smaltimento del materiale di risulta.",
    image:
      "https://images.unsplash.com/photo-1710492873117-b790ba523fd4?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  },
  {
    n: "03",
    title: "Progettazione",
    Icon: PenTool,
    desc: "Progetti su misura: dal piccolo terrazzo al giardino paesaggistico, in dialogo con il luogo.",
    long:
      "Progetto giardini e spazi verdi su misura, dal piccolo terrazzo al giardino paesaggistico. Studio esposizione, terreno e stile di vita per creare un ambiente equilibrato, con la scelta delle piante giuste e un disegno di progetto dettagliato prima di ogni intervento.",
    image: "/brand/progettazione.png",
  },
  {
    n: "04",
    title: "Impianti di Irrigazione",
    Icon: Droplets,
    desc: "Studio, installazione e manutenzione di sistemi automatici efficienti, con risparmio idrico.",
    long:
      "Studio, installo e mantengo impianti di irrigazione automatici ed efficienti. Programmazione intelligente, irrigatori e ali gocciolanti per portare l'acqua giusta a ogni pianta, riducendo sprechi e consumi — un giardino sempre idratato, anche quando non ci sei.",
    image:
      "https://images.unsplash.com/photo-1541955193702-9ca03b1bb11a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  },
  {
    n: "05",
    title: "Cura delle Piante",
    Icon: Leaf,
    desc: "Diagnosi, trattamenti, concimazioni e consulenza per piante in piena salute.",
    long:
      "Diagnosi e trattamento di malattie e parassiti, concimazioni mirate e consulenza personalizzata. Mi prendo cura della salute delle tue piante con prodotti adeguati e un approccio attento all'equilibrio naturale del giardino, per farle crescere forti e rigogliose.",
    image:
      "https://images.unsplash.com/photo-1627730327661-9b5efb7d47b9?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  },
];

export default function Services() {
  const [selected, setSelected] = useState(null);

  const open = (s) => setSelected(s);
  const goContact = () => {
    setSelected(null);
    setTimeout(
      () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }),
      250
    );
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#EFECE5]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <p className="overline mb-6">— I miei servizi</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A3628]">
              Cosa posso <br />
              <span className="font-accent italic font-normal">fare</span> per
              il tuo verde.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-12">
            <p className="text-base md:text-lg leading-relaxed text-[#5C6B62]">
              {"Ogni servizio è pensato per durare. Lavoro con metodo, attrezzatura professionale e una visione d'insieme — perché un giardino è un organismo vivo, non solo un'immagine."}
            </p>
            <p className="mt-4 text-sm text-[#1A3628]/70 italic font-accent">
              Clicca su un servizio per scoprire di più.
            </p>
          </div>
        </div>

        <div className="border-t border-[#D5D1C8]">
          {SERVICES.map((s, i) => (
            <button
              key={s.n}
              data-testid={`service-row-${i}`}
              onClick={() => open(s)}
              className="service-row w-full text-left py-8 md:py-10 grid grid-cols-12 gap-6 items-center group"
            >
              <span className="col-span-2 md:col-span-1 font-accent italic text-2xl text-[#5C6B62]">
                {s.n}
              </span>
              <h3 className="col-span-10 md:col-span-4 font-serif-display text-2xl md:text-3xl text-[#1A3628] tracking-tight">
                {s.title}
              </h3>
              <p className="hidden md:block md:col-span-6 text-base text-[#5C6B62] leading-relaxed pr-12">
                {s.desc}
              </p>
              <span className="col-span-12 md:col-span-1 flex md:justify-end mt-2 md:mt-0">
                <span className="inline-flex items-center justify-center w-10 h-10 border border-[#1A3628]/30 group-hover:bg-[#1A3628] group-hover:text-[#FAF9F6] transition-colors duration-500">
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </span>
              </span>
              <p className="md:hidden col-span-12 text-sm text-[#5C6B62] leading-relaxed -mt-2">
                {s.desc}
              </p>
            </button>
          ))}
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent
          side="right"
          data-testid="service-detail-panel"
          className="w-full sm:max-w-md bg-[#FAF9F6] border-l border-[#1A3628]/15 p-0 overflow-y-auto"
        >
          {selected && (
            <div className="flex flex-col h-full">
              <div className="relative image-hover-wrap h-56 shrink-0">
                <img
                  src={selected.image}
                  alt={selected.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A3628]/50 to-transparent" />
                <span className="absolute top-5 left-6 inline-flex items-center justify-center w-12 h-12 bg-[#FAF9F6] text-[#1A3628] shadow-md">
                  <selected.Icon size={22} strokeWidth={1.5} />
                </span>
              </div>

              <div className="p-6 md:p-8 flex flex-col flex-1">
                <SheetHeader className="text-left space-y-0">
                  <p className="overline mb-3">Servizio {selected.n}</p>
                  <SheetTitle className="font-serif-display text-3xl text-[#1A3628] tracking-tight font-normal">
                    {selected.title}
                  </SheetTitle>
                </SheetHeader>

                <p className="mt-6 text-base leading-relaxed text-[#5C6B62]">
                  {selected.long}
                </p>

                <div className="mt-auto pt-8">
                  <button
                    data-testid="service-panel-cta"
                    onClick={goContact}
                    className="btn-primary w-full justify-center"
                  >
                    Richiedi un preventivo
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}
